import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Chip,
  Badge
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  Map as MapIcon,
  Notifications as AlertIcon,
  Settings as SettingsIcon,
  People as UsersIcon,
  Diamond as DiamondIcon,
  BarChart as StatsIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAssets } from '../../contexts/AssetsContext';
import { useAlerts } from '../../contexts/AlertsContext';
import AdminMap from '../../components/admin/AdminMap';
import AlertMap from '../../components/admin/AlertMap';
import AlertFeed from '../../components/admin/AlertFeed';
import AssetManagement from '../../components/admin/AssetManagement';
import UserManagement from '../../components/admin/UserManagement';
import AlertManagement from '../../components/admin/AlertManagement';
import DashboardStats from '../../components/admin/DashboardStats';

const AdminDashboard = () => {
  const { currentUser, logout } = useAuth();
  const { assets } = useAssets();
  const { activeAlerts } = useAlerts();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeAlertsCount, setActiveAlertsCount] = useState(0);
  
  useEffect(() => {
    // Count active alerts
    const alertCount = activeAlerts.length;
    setActiveAlertsCount(alertCount);
  }, [activeAlerts]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawerContent = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'gray.900', mb: 1 }}>
          Jewelry Tracker
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Admin Control Panel
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'gray.200' }} />
      <List sx={{ px: 2, py: 2 }}>
        <ListItem 
          button 
          onClick={() => setTabValue(0)} 
          selected={tabValue === 0}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <DashboardIcon color={tabValue === 0 ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Dashboard" 
            primaryTypographyProps={{ 
              fontWeight: tabValue === 0 ? 600 : 500,
              color: tabValue === 0 ? 'primary.700' : 'gray.700'
            }} 
          />
        </ListItem>
        <ListItem 
          button 
          onClick={() => setTabValue(1)} 
          selected={tabValue === 1}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <MapIcon color={tabValue === 1 ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Global Map" 
            primaryTypographyProps={{ 
              fontWeight: tabValue === 1 ? 600 : 500,
              color: tabValue === 1 ? 'primary.700' : 'gray.700'
            }} 
          />
        </ListItem>
        <ListItem 
          button 
          onClick={() => setTabValue(2)} 
          selected={tabValue === 2}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <DiamondIcon color={tabValue === 2 ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Asset Management" 
            primaryTypographyProps={{ 
              fontWeight: tabValue === 2 ? 600 : 500,
              color: tabValue === 2 ? 'primary.700' : 'gray.700'
            }} 
          />
        </ListItem>
        <ListItem 
          button
          onClick={() => setTabValue(3)}
          selected={tabValue === 3}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <UsersIcon color={tabValue === 3 ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Users" 
            primaryTypographyProps={{ 
              fontWeight: tabValue === 3 ? 600 : 500,
              color: tabValue === 3 ? 'primary.700' : 'gray.700'
            }} 
          />
        </ListItem>
        <ListItem 
          button
          onClick={() => setTabValue(4)}
          selected={tabValue === 4}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <Badge badgeContent={activeAlertsCount} color="error" invisible={activeAlertsCount === 0}>
              <AlertIcon color={tabValue === 4 ? 'primary' : 'inherit'} />
            </Badge>
          </ListItemIcon>
          <ListItemText 
            primary="Alerts" 
            primaryTypographyProps={{ 
              fontWeight: tabValue === 4 ? 600 : 500,
              color: tabValue === 4 ? 'primary.700' : 'gray.700'
            }} 
          />
        </ListItem>
        <ListItem 
          button
          onClick={() => setTabValue(5)}
          selected={tabValue === 5}
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon>
            <SettingsIcon color={tabValue === 5 ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Settings" 
            primaryTypographyProps={{ 
              fontWeight: 500,
              color: 'gray.700'
            }} 
          />
        </ListItem>
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ borderColor: 'gray.200' }} />
      <List sx={{ px: 2, py: 2 }}>
        <ListItem 
          button 
          onClick={logout}
          sx={{ borderRadius: 2, color: 'error.600' }}
        >
          <ListItemIcon>
            <LogoutIcon color="error" />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{ 
              fontWeight: 500,
              color: 'error.600'
            }} 
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <IconButton
            size="medium"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2, color: 'gray.700' }}
            onClick={toggleDrawer}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'primary.600' }}>
            Admin Control Room
          </Typography>
          {activeAlerts.length > 0 && (
            <IconButton sx={{ mr: 2, bgcolor: 'error.50', color: 'error.600', borderRadius: 2 }}>
              <AlertIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>
                {activeAlerts.length}
              </Typography>
            </IconButton>
          )}
          <Button 
            variant="text"
            color="inherit" 
            onClick={logout} 
            startIcon={<LogoutIcon />}
            sx={{ color: 'gray.600' }}
          >
            {isMobile ? '' : 'Logout'}
          </Button>
        </Toolbar>
        <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="admin navigation tabs"
            sx={{ 
              '& .MuiTab-root': { 
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                py: 2
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Global Map" />
            <Tab label="Asset Management" />
          </Tabs>
        </Box>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            borderRight: '1px solid',
            borderColor: 'divider',
            boxShadow: 3
          }
        }}
      >
        {drawerContent}
      </Drawer>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {/* Dashboard Tab */}
        {tabValue === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StatsIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                      Dashboard Overview
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${activeAlertsCount} active alerts`} 
                    color="error" 
                    size="small"
                    variant="outlined"
                    icon={<AlertIcon fontSize="small" />}
                  />
                </Box>
                <Box sx={{ p: 3 }}>
                  {assets && activeAlerts && <DashboardStats />}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Global Map Tab */}
        {tabValue === 1 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                  <MapIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                    Global Asset Map
                  </Typography>
                </Box>
                <Box sx={{ height: 700 }}>
                  {assets && activeAlerts && (
                    <AlertMap assets={assets} alerts={activeAlerts} fullScreen />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Asset Management Tab */}
        {tabValue === 2 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                  <DiamondIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                    Asset Management
                  </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  {assets && <AssetManagement />}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* User Management Tab */}
        {tabValue === 3 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                  <UsersIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                    User Management
                  </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  {assets && activeAlerts && <UserManagement />}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Alert Management Tab */}
        {tabValue === 4 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AlertIcon sx={{ mr: 1.5, color: 'error.main' }} />
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                      Alert Management
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${activeAlertsCount} active alerts`} 
                    color="error" 
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ p: 3 }}>
                  {assets && activeAlerts && <AlertManagement />}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Settings Tab */}
        {tabValue === 5 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                    System Settings
                  </Typography>
                </Box>
                <Box sx={{ p: 4 }}>
                  <Typography variant="body1">
                    Settings panel is under development.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default AdminDashboard;
